
export enum GasUnit {
    ppm = 'ppm',
    percent = 'percent',
}

export enum GasType {
    o2 = 'o2',
    co = 'co',
    h2s = 'h2s',
    nh3 = 'nh3',
    lel = 'lel',
    so2 = 'so2',
}

export interface GasSetting {
    gas: GasType;
    unit: GasUnit;
    thresholds: GasThresholds;
}

export interface GasThresholds {
    lowerPanic?: number;
    lowerWarn?: number;
    upperWarn: number;
    upperPanic: number;
}

export interface SensorGasItem {
    name: string;
    id: SensorGasCode;
}

export interface GasMonitorReadingHistory extends GasMonitorReading {
    date: Date;
}

export interface GasMonitorReading {
    id?: string;
    serialNumber?: string;
    status?: InstrumentStatus;
    readings?: GasSensorReading[];
    updated?: Date;
    account?: string;
    accountName?: string;
}

export interface GasSensorReading {
    status: SensorStatus;
    code: SensorGasCode;
    value?: number;
    unit?: SensorUnit;
    time?: number | null;
}

export enum SensorUnit {
    Ppm = 'ppm',
    PercentVolume = '%vol',
    PercentLel = '%LEL',
}

export interface GasSensorStatus extends GasSensorReading {
    date: Date;
}

export enum InstrumentStatus {
    Normal = 'normal',
    Panic = 'panic',
    Shutdown = 'shutdown',
    ManDown = 'man-down',
    PumpFault = 'pump-fault',
    LowBattery = 'low-battery',
    Charging = 'charging',
    Disconnected = 'disconnected',
    Connecting = 'connecting',
    Alarm = 'alarm',
    Unpaired = 'unpaired',
}

export enum SensorStatus {
    Normal = 'normal',
    LowAlarm = 'low-alarm',
    HighAlarm = 'high-alarm',
    Fault = 'fault',
    TwaAlarm = 'twa-alarm',
    StelAlarm = 'stel-alarm',
}

export enum SensorGasCode {
    Ammonia = 'NH3',
    Ammonia2 = 'CH3?',
    CarbonDioxide = 'CO2',
    CarbonMonoxide = 'CO',
    Chlorine = 'Cl',
    ChlorineDioxide = 'ClO2',
    Hexane = 'C6H14',
    Hydrogen = 'H',
    HydrogenChloride = 'HCl',
    HydrogenCyanide = 'HCN',
    HydrogenFluoride = 'HF',
    HydrogenSulfide = 'H2S',
    Methane = 'CH4',
    NitricOxide = 'NO',
    NitrogenDioxide = 'NO2',
    Oxygen = 'O2',
    Ozone = 'O3',
    Pentane = 'C5H12',
    Phosgene = 'COCl2',
    Phosphine = 'PH3',
    PhotoIonizationDetector = 'PID',
    Propane = 'C3H8',
    SulfurDioxide = 'SO2',
    UnidentifiedCombustibleGas = 'Comb',
}

export const InstrumentStatusTranslationKey: { [key in InstrumentStatus]?: string } = {
    [InstrumentStatus.ManDown]: 'shared.gas.man-down',
    [InstrumentStatus.Panic]: 'shared.gas.panic',
    [InstrumentStatus.PumpFault]: 'shared.gas.fault',
};

export const SensorStatusToColor: { [key in SensorStatus]?: string } = {
    [SensorStatus.Normal]: 'info',
    [SensorStatus.LowAlarm]: 'warning',
    [SensorStatus.HighAlarm]: 'danger',
    [SensorStatus.Fault]: 'danger',
    [SensorStatus.TwaAlarm]: 'danger',
    [SensorStatus.StelAlarm]: 'danger',
};

export const getSensorUnit = (unit: SensorUnit) => [SensorUnit.PercentLel, SensorUnit.PercentVolume].includes(unit) ? '%' : unit;

export const getLowestSensorStatus = (readings: GasSensorReading[]) => {
    if (!readings?.length) return SensorStatus.Normal;

    return readings.reduce((acc, r) => {
        if (r.status === SensorStatus.HighAlarm) return SensorStatus.HighAlarm;
        if (r.status === SensorStatus.LowAlarm) return SensorStatus.LowAlarm;
        if (r.status === SensorStatus.Fault) return SensorStatus.Fault;
        if (r.status === SensorStatus.Normal) return acc;
        return SensorStatus.LowAlarm;
    }, SensorStatus.Normal);
};